import { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { Bar, Doughnut, Line, PolarArea } from "react-chartjs-2";
import "chart.js/auto";
import { useTranslation } from "react-i18next";
import {
  LuClipboardList,
  LuMessageSquare,
  LuPackageSearch,
  LuUsers,
} from "react-icons/lu";
import {
  fetchClaimsPerMonthStats,
  fetchClientInteractionsByMonthStats,
  fetchLostFoundStatusStats,
  fetchMySupportsPriorityStats,
} from "../../../services/DashboardStatsService";

const MONTH_WINDOW_OPTIONS = [6, 12, 24];
const DAYS_WINDOW_OPTIONS = [30, 60, 90, 180, 365];

const PRIORITY_ORDER = ["faible", "moyenne", "haute", "critique"];
const PRIORITY_COLORS = {
  faible: "#22c55e",
  moyenne: "#eab308",
  haute: "#f97316",
  critique: "#ef4444",
};

const LOST_FOUND_COLORS = {
  found: "#94a3b8",
  returned: "#22c55e",
};

function formatMonthLabel(monthKey, locale) {
  if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) return monthKey || "";
  const [y, m] = monthKey.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, 1));
  return date.toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  });
}

function ChartCard({
  icon: Icon,
  title,
  subtitle,
  filter,
  headerExtra,
  loading,
  empty,
  emptyLabel,
  children,
  className = "",
}) {
  return (
    <div
      className={`card dashboard-section-card google-review-analytics-card h-100 w-100 mb-0 ${className}`}
    >
      <div className="card-header border-0 pb-0">
        <div className="dashboard-chart-header-shell">
          <div className="dashboard-chart-header-top">
            <h4 className="text-black fs-20 mb-0 d-flex align-items-center gap-2">
              <Icon
                className="dashboard-chart-title-icon flex-shrink-0"
                strokeWidth={1.85}
                aria-hidden
              />
              {title}
            </h4>
            {filter ? (
              <div className="dashboard-chart-header-filters ops-chart-filter-end">
                {filter.control}
              </div>
            ) : null}
          </div>
          {subtitle ? <p className="fs-13 mb-0 text-muted">{subtitle}</p> : null}
          {headerExtra || null}
        </div>
      </div>
      <div className="card-body pt-3">
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" size="sm" className="text-primary" />
          </div>
        ) : empty ? (
          <div className="google-review-empty text-muted py-4 text-center rounded-3">
            {emptyLabel}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function WindowSelect({ value, onChange, options, labelFn, ariaLabel }) {
  return (
    <Form.Select
      size="sm"
      className="ops-chart-select"
      value={value}
      aria-label={ariaLabel}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {labelFn(opt)}
        </option>
      ))}
    </Form.Select>
  );
}

function KpiPill({ label, value }) {
  return (
    <div className="ops-chart-kpi">
      <span className="ops-chart-kpi-label">{label}</span>
      <strong className="ops-chart-kpi-value">{value}</strong>
    </div>
  );
}

const tooltipBase = {
  backgroundColor: "rgba(15, 23, 42, 0.92)",
  padding: 10,
  cornerRadius: 8,
  titleFont: { size: 12, weight: "600" },
  bodyFont: { size: 12 },
};

const lineDefaults = (maxY, color) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: tooltipBase,
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#64748b", font: { size: 11 } },
      border: { display: false },
    },
    y: {
      beginAtZero: true,
      suggestedMax: Math.max(maxY, 1),
      ticks: { precision: 0, color: "#94a3b8", font: { size: 11 } },
      grid: { color: "rgba(148, 163, 184, 0.2)" },
      border: { display: false },
    },
  },
  elements: {
    line: { borderColor: color, borderWidth: 2.75, tension: 0.4 },
    point: {
      radius: 3.5,
      hoverRadius: 6,
      backgroundColor: "#fff",
      borderColor: color,
      borderWidth: 2,
    },
  },
});

const verticalBarDefaults = (maxY, { stacked = false } = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: stacked
      ? {
          display: true,
          position: "bottom",
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
            pointStyle: "rectRounded",
            color: "#475569",
            font: { size: 11 },
            padding: 16,
          },
        }
      : { display: false },
    tooltip: tooltipBase,
  },
  scales: {
    x: {
      stacked,
      grid: { display: false },
      ticks: { color: "#64748b", font: { size: 11 }, maxRotation: 0 },
      border: { display: false },
    },
    y: {
      stacked,
      beginAtZero: true,
      suggestedMax: Math.max(maxY, 1),
      ticks: { precision: 0, color: "#94a3b8", font: { size: 11 } },
      grid: { color: "rgba(148, 163, 184, 0.2)" },
      border: { display: false },
    },
  },
});

const doughnutDefaults = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "68%",
  plugins: {
    legend: { display: false },
    tooltip: tooltipBase,
  },
};

const polarDefaults = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: tooltipBase,
  },
  scales: {
    r: {
      beginAtZero: true,
      ticks: { display: false },
      grid: { color: "rgba(148, 163, 184, 0.25)" },
      angleLines: { color: "rgba(148, 163, 184, 0.2)" },
    },
  },
};

export default function DashboardOpsCharts() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("fr") ? "fr" : "en";

  const [claimsMonths, setClaimsMonths] = useState(12);
  const [lostFoundDays, setLostFoundDays] = useState(90);
  const [priorityDays, setPriorityDays] = useState(90);
  const [interactionsMonths, setInteractionsMonths] = useState(12);

  const [claimsStats, setClaimsStats] = useState({ series: [], total: 0 });
  const [lostFoundStats, setLostFoundStats] = useState({ series: [], total: 0 });
  const [priorityStats, setPriorityStats] = useState({ series: [], total: 0 });
  const [interactionStats, setInteractionStats] = useState({
    series: [],
    total: 0,
  });

  const [claimsLoading, setClaimsLoading] = useState(true);
  const [lostFoundLoading, setLostFoundLoading] = useState(true);
  const [priorityLoading, setPriorityLoading] = useState(true);
  const [interactionsLoading, setInteractionsLoading] = useState(true);

  const loadClaims = useCallback(async () => {
    setClaimsLoading(true);
    try {
      const data = await fetchClaimsPerMonthStats(claimsMonths);
      setClaimsStats({
        series: Array.isArray(data?.series) ? data.series : [],
        total: Number(data?.total) || 0,
      });
    } catch (err) {
      console.error("claims per month:", err);
      setClaimsStats({ series: [], total: 0 });
    } finally {
      setClaimsLoading(false);
    }
  }, [claimsMonths]);

  const loadLostFound = useCallback(async () => {
    setLostFoundLoading(true);
    try {
      const data = await fetchLostFoundStatusStats(lostFoundDays);
      setLostFoundStats({
        series: Array.isArray(data?.series) ? data.series : [],
        total: Number(data?.total) || 0,
      });
    } catch (err) {
      console.error("lost found status:", err);
      setLostFoundStats({ series: [], total: 0 });
    } finally {
      setLostFoundLoading(false);
    }
  }, [lostFoundDays]);

  const loadPriority = useCallback(async () => {
    setPriorityLoading(true);
    try {
      const data = await fetchMySupportsPriorityStats(priorityDays);
      setPriorityStats({
        series: Array.isArray(data?.series) ? data.series : [],
        total: Number(data?.total) || 0,
      });
    } catch (err) {
      console.error("support priority:", err);
      setPriorityStats({ series: [], total: 0 });
    } finally {
      setPriorityLoading(false);
    }
  }, [priorityDays]);

  const loadInteractions = useCallback(async () => {
    setInteractionsLoading(true);
    try {
      const data = await fetchClientInteractionsByMonthStats(interactionsMonths);
      setInteractionStats({
        series: Array.isArray(data?.series) ? data.series : [],
        total: Number(data?.total) || 0,
      });
    } catch (err) {
      console.error("client interactions:", err);
      setInteractionStats({ series: [], total: 0 });
    } finally {
      setInteractionsLoading(false);
    }
  }, [interactionsMonths]);

  useEffect(() => {
    loadClaims();
  }, [loadClaims]);
  useEffect(() => {
    loadLostFound();
  }, [loadLostFound]);
  useEffect(() => {
    loadPriority();
  }, [loadPriority]);
  useEffect(() => {
    loadInteractions();
  }, [loadInteractions]);

  const claimsMax = useMemo(
    () => Math.max(1, ...claimsStats.series.map((s) => Number(s.count) || 0)),
    [claimsStats]
  );

  const claimsLineData = useMemo(
    () => ({
      labels: claimsStats.series.map((s) => formatMonthLabel(s.month, locale)),
      datasets: [
        {
          label: t("opsClaimsPerMonthSeries"),
          data: claimsStats.series.map((s) => Number(s.count) || 0),
          fill: true,
          backgroundColor: "rgba(232, 137, 74, 0.18)",
          borderColor: "#E8894A",
        },
      ],
    }),
    [claimsStats, locale, t]
  );

  const orderedPriority = useMemo(
    () =>
      PRIORITY_ORDER.map((key) => {
        const row = priorityStats.series.find((s) => s.priority === key);
        return { priority: key, count: Number(row?.count) || 0 };
      }),
    [priorityStats]
  );

  const lostFoundDonutData = useMemo(() => {
    const series = lostFoundStats.series || [];
    const hasData = series.some((s) => (Number(s.count) || 0) > 0);
    return {
      labels: series.map((s) =>
        s.status === "returned" ? t("lostFoundReturned") : t("lostFoundFound")
      ),
      datasets: [
        {
          data: hasData
            ? series.map((s) => Number(s.count) || 0)
            : [1],
          backgroundColor: hasData
            ? series.map((s) => LOST_FOUND_COLORS[s.status] || "#64748b")
            : ["#e2e8f0"],
          borderWidth: 0,
          hoverOffset: 6,
        },
      ],
    };
  }, [lostFoundStats, t]);

  const priorityPolarData = useMemo(
    () => ({
      labels: orderedPriority.map((s) => t(`supportPriority_${s.priority}`)),
      datasets: [
        {
          data: orderedPriority.map((s) => s.count),
          backgroundColor: orderedPriority.map(
            (s) => `${PRIORITY_COLORS[s.priority] || "#64748b"}cc`
          ),
          borderWidth: 0,
        },
      ],
    }),
    [orderedPriority, t]
  );

  const interactionRowTotal = (row) => Number(row.claim) || 0;

  const interactionsVisibleTotal = useMemo(
    () =>
      interactionStats.series.reduce(
        (sum, row) => sum + interactionRowTotal(row),
        0
      ),
    [interactionStats]
  );

  const interactionsMax = useMemo(
    () =>
      Math.max(1, ...interactionStats.series.map((row) => interactionRowTotal(row))),
    [interactionStats]
  );

  const interactionsStackedData = useMemo(
    () => ({
      labels: interactionStats.series.map((s) => formatMonthLabel(s.month, locale)),
      datasets: [
        {
          label: t("opsInteractionsClaimSeries"),
          data: interactionStats.series.map((s) => Number(s.claim) || 0),
          backgroundColor: "#E8894A",
          borderRadius: 4,
          stack: "interactions",
        },
      ],
    }),
    [interactionStats, locale, t]
  );

  const monthsLabel = (n) => t("opsMonthsWindowOption", { count: n });
  const daysLabel = (n) => t("dashboardRollingDaysOption", { count: n });

  const lostFoundFound = Number(
    lostFoundStats.series.find((s) => s.status === "found")?.count || 0
  );
  const lostFoundReturned = Number(
    lostFoundStats.series.find((s) => s.status === "returned")?.count || 0
  );

  return (
    <>
      <div className="col-12">
        <ChartCard
          icon={LuClipboardList}
          title={t("opsClaimsPerMonthTitle")}
          subtitle={t("opsClaimsPerMonthSubtitle", { months: claimsMonths })}
          filter={{
            label: t("opsMonthsWindowLabel"),
            control: (
              <WindowSelect
                value={claimsMonths}
                onChange={setClaimsMonths}
                options={MONTH_WINDOW_OPTIONS}
                labelFn={monthsLabel}
                ariaLabel={t("opsMonthsWindowLabel")}
              />
            ),
          }}
          loading={claimsLoading}
          empty={claimsStats.total === 0}
          emptyLabel={t("opsClaimsPerMonthEmpty")}
        >
          <div className="ops-chart-meta mb-3">
            <KpiPill label={t("opsTotalLabel")} value={claimsStats.total} />
          </div>
          <div className="db-chart-wrap google-review-chart-wrap" style={{ minHeight: 300 }}>
            <Line
              data={claimsLineData}
              options={lineDefaults(claimsMax, "#E8894A")}
            />
          </div>
        </ChartCard>
      </div>

      <div className="col-12 col-xl-6 d-flex">
        <ChartCard
          icon={LuPackageSearch}
          title={t("opsLostFoundStatusTitle")}
          subtitle={t("opsLostFoundStatusSubtitle", { days: lostFoundDays })}
          filter={{
            label: t("opsDaysWindowLabel"),
            control: (
              <WindowSelect
                value={lostFoundDays}
                onChange={setLostFoundDays}
                options={DAYS_WINDOW_OPTIONS}
                labelFn={daysLabel}
                ariaLabel={t("opsDaysWindowLabel")}
              />
            ),
          }}
          loading={lostFoundLoading}
          empty={lostFoundStats.total === 0}
          emptyLabel={t("opsLostFoundStatusEmpty")}
        >
          <div className="ops-split-layout ops-split-layout--compact">
            <div className="ops-donut-panel">
              <div className="ops-donut-wrap">
                <Doughnut data={lostFoundDonutData} options={doughnutDefaults} />
                <div className="ops-donut-center">
                  <span className="ops-donut-center-value">
                    {lostFoundStats.total}
                  </span>
                  <span className="ops-donut-center-label">
                    {t("opsTotalLabel")}
                  </span>
                </div>
              </div>
            </div>
            <div className="ops-legend-panel">
              <div className="ops-legend-row">
                <span
                  className="ops-legend-swatch"
                  style={{ background: LOST_FOUND_COLORS.found }}
                />
                <span className="ops-legend-label">{t("lostFoundFound")}</span>
                <strong className="ops-legend-value">{lostFoundFound}</strong>
              </div>
              <div className="ops-legend-row">
                <span
                  className="ops-legend-swatch"
                  style={{ background: LOST_FOUND_COLORS.returned }}
                />
                <span className="ops-legend-label">{t("lostFoundReturned")}</span>
                <strong className="ops-legend-value">{lostFoundReturned}</strong>
              </div>
            </div>
          </div>
        </ChartCard>
      </div>

      <div className="col-12 col-xl-6 d-flex">
        <ChartCard
          icon={LuMessageSquare}
          title={t("opsRequestsPriorityTitle")}
          subtitle={t("opsRequestsPrioritySubtitle", { days: priorityDays })}
          filter={{
            label: t("opsDaysWindowLabel"),
            control: (
              <WindowSelect
                value={priorityDays}
                onChange={setPriorityDays}
                options={DAYS_WINDOW_OPTIONS}
                labelFn={daysLabel}
                ariaLabel={t("opsDaysWindowLabel")}
              />
            ),
          }}
          loading={priorityLoading}
          empty={priorityStats.total === 0}
          emptyLabel={t("opsRequestsPriorityEmpty")}
        >
          <div className="ops-split-layout ops-split-layout--compact">
            <div className="ops-donut-panel">
              <div
                className="db-chart-wrap google-review-chart-wrap"
                style={{ minHeight: 220 }}
              >
                <PolarArea data={priorityPolarData} options={polarDefaults} />
              </div>
            </div>
            <div className="ops-legend-panel">
              <div className="ops-chart-meta mb-2">
                <KpiPill label={t("opsTotalLabel")} value={priorityStats.total} />
              </div>
              {orderedPriority.map((s) => (
                <div className="ops-legend-row" key={s.priority}>
                  <span
                    className="ops-legend-swatch"
                    style={{ background: PRIORITY_COLORS[s.priority] }}
                  />
                  <span className="ops-legend-label">
                    {t(`supportPriority_${s.priority}`)}
                  </span>
                  <strong className="ops-legend-value">{s.count}</strong>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>
      </div>

      <div className="col-12">
        <ChartCard
          icon={LuUsers}
          title={t("opsInteractionsTitle")}
          subtitle={t("opsInteractionsSubtitle", {
            months: interactionsMonths,
          })}
          filter={{
            label: t("opsMonthsWindowLabel"),
            control: (
              <WindowSelect
                value={interactionsMonths}
                onChange={setInteractionsMonths}
                options={MONTH_WINDOW_OPTIONS}
                labelFn={monthsLabel}
                ariaLabel={t("opsMonthsWindowLabel")}
              />
            ),
          }}
          loading={interactionsLoading}
          empty={interactionsVisibleTotal === 0}
          emptyLabel={t("opsInteractionsEmpty")}
        >
          <div className="ops-chart-meta mb-3">
            <KpiPill label={t("opsTotalLabel")} value={interactionsVisibleTotal} />
          </div>
          <div className="db-chart-wrap google-review-chart-wrap" style={{ minHeight: 300 }}>
            <Bar
              data={interactionsStackedData}
              options={verticalBarDefaults(interactionsMax, { stacked: true })}
            />
          </div>
        </ChartCard>
      </div>
    </>
  );
}
